import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { Evaluation } from '@teetsh/app/src/containers/CarnetsDeNotes/types/carnetsDeNotes';
import { serializeEvaluation } from '@teetsh/app/src/containers/CarnetsDeNotes/queries/serializer';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { cdnKeys } from '@teetsh/app/src/containers/CarnetsDeNotes/queries/keys';
import { useTranslation } from 'react-i18next';
import { useEvaluationHistory } from './evaluationHistoryAtoms';

function updateEvaluation({
  schoolyearId,
  schoolId,
  cdnId,
  evaluation,
}: {
  schoolyearId: string;
  schoolId: string;
  cdnId: string;
  evaluation: Evaluation;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/carnets-de-notes/${cdnId}/evaluations/${evaluation.id}`;
  return request(requestURL, {
    method: 'PATCH',
    body: serializeEvaluation(evaluation),
  });
}

export function useUpdateEvaluation(
  carnetDeNotesId: string,
  evaluationId: string,
  disableUndoRedo: boolean = false,
) {
  const { notifyError } = useNotification();
  const { schoolyear, school } = useSchoolSchoolyear();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['carnet']);

  const { addToPast, resetFuture } = useEvaluationHistory(evaluationId);

  const {
    mutateAsync,
    isPending: isLoading,
    status,
  } = useMutation({
    mutationFn: (evaluation: Evaluation) =>
      updateEvaluation({
        schoolId: school.id,
        schoolyearId: schoolyear.id,
        cdnId: carnetDeNotesId,
        evaluation,
      }),
    onMutate: evaluation => {
      if (!disableUndoRedo) {
        const oldEvaluation = queryClient.getQueryData<Evaluation>(
          cdnKeys.cdnEvaluationDetails(
            schoolyear.id,
            school.id,
            carnetDeNotesId,
            evaluation.id,
          ),
        );

        if (oldEvaluation) {
          addToPast(oldEvaluation);
        }
        resetFuture();
      }

      queryClient.setQueryData<Evaluation>(
        cdnKeys.cdnEvaluationDetails(
          schoolyear.id,
          school.id,
          carnetDeNotesId,
          evaluation.id,
        ),
        evaluation,
      );
      queryClient.setQueryData<Evaluation[]>(
        cdnKeys.cdnEvaluations(schoolyear.id, school.id, carnetDeNotesId),
        evals =>
          (evals || []).map(ev => {
            if (ev.id === evaluation.id) {
              return evaluation;
            }
            return ev;
          }),
      );
    },
    onError: (_, evaluation) => {
      queryClient.invalidateQueries({
        queryKey: cdnKeys.cdnEvaluationDetails(
          schoolyear.id,
          school.id,
          carnetDeNotesId,
          evaluation.id,
        ),
      });
      notifyError({
        title: t('carnet:messages.evaluation.updateError'),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cdnKeys.cdnEvaluationsOmittedInLivret(
          schoolyear.id,
          school.id,
          carnetDeNotesId,
        ),
      });
    },
  });
  return { update: mutateAsync, isLoading, status };
}
